import { create, UseBoundStore, StoreApi, StateCreator, Mutate } from 'zustand'

import { RequiredKeys } from '@/types/type-handlers'

export type ReactStore<T> = UseBoundStore<Mutate<StoreApi<T>, []>> & { use: T }

/**
 * Creates a React store based on Zustand's core logic with an enhanced API to access the states
 * inside React application.
 *
 * This function simplifies the creation of stores in your React application by abstracting
 * away the core logic behind state management, while providing a clean interface for manipulating
 * and accessing the state.
 *
 * The goal is to enable easy sharing of state between pages, components, hooks and helpers,
 * while offering flexibility in terms of customization and maintainability.
 *
 * @example
 *
 * Creating a store:
 * ```ts
 * // counter.store.ts
 * export const CounterStore = createReactStore<{ count: number }>(() => ({ count: 0 }))
 * ```
 *
 * Creating actions for the store:
 * ```ts
 * // actions.ts
 * export const increment = () => CounterStore.setState(({ count }) => ({ count: count + 1 }))
 * export const decrement = () => CounterStore.setState(({ count }) => ({ count: count - 1 }))
 * ```
 *
 * Accessing store inside a React component:
 * ```ts
 * // Counter.tsx
 * export function Counter() {
 *   const { count } = CounterStore.use
 *   return <div>
 *     <h1>{count}</h1>
 *     <button type="button" onClick={decrement}>-1</button>
 *     <button type="button" onClick={increment}>+1</button>
 *   </div>
 * }
 * ```
 *
 * Accessing calculated values:
 * ```ts
 * // DozenCounter.tsx
 * export function DozenCounter() {
 *   const dozensCount = CounterStore(({ count }) => count % 10)
 *   return <div>
 *     <h1>Dozens: {dozensCount}</h1>
 *     <button type="button" onClick={decrement}>-1</button>
 *     <button type="button" onClick={increment}>+1</button>
 *   </div>
 * }
 * ```
 * Calculating values using selectors prevents unnecessary re-renders, triggering a render
 * only when the calculated value has changed.
 *
 * **Note**: Even if a value is undefined, it should be defined when creating the store to be recognized
 * as a consumable react state, otherwise you will get issues when accessing the values through `Store.use`
 *
 * **Why use this function instead of directly using Zustand's `create`?**
 *
 * It provides a more flexible API that allows we to enhance and extend the store's
 * functionality without altering its core logic. Decoupling store creation from state
 * management logic, we can easily swap out or modify the store's core behavior, if needed,
 * making it easier to adapt to future requirements. Also, the function reduces verbosity and
 * simplifies state management patterns, making it easier for developers to work with stores,
 * while avoiding the complexities of setting up Zustand middleware such as shallows or dealing
 * with manual state access like individual selector for each state.
 *
 * **Why separate setters/actions from the store creation?**
 *
 * The setters/actions should be defined outside the store creation to maintain a cleaner and more
 * organized structure. The store is solely responsible for managing state, while actions handle
 * business logic and side effects. This design makes it easier to update, refactor, and test each
 * part independently, improving long-term maintainability. By Isolating actions from the store allows
 * we to test them separately without involving the entire store, making our tests more focused and manageable.
 *
 * @template T Store type
 * @template I (auto infer) Store required properties (required to identify the keys for the `use` definition)
 * @param initializer A function that initializes the store's state.
 */
export function createReactStore<
  T extends object,
  I extends T = RequiredKeys<T>,
>(initializer: StateCreator<I>): ReactStore<I> {
  const _store = create(initializer) as UseBoundStore<
    Mutate<StoreApi<I>, []>
  > & { use: I }
  const store = Object.assign(_store, { use: {} } as { use: I })

  ;(Object.keys(store.getState()) as Array<keyof I>).forEach((key) => {
    Object.defineProperty(store.use, key, {
      get() {
        return store(({ [key]: value }) => value)
      },
    })
  })

  return store
}
